import formatLocaleNumber, { formatLocaleCurrencyParts, narrowCurrencySymbolFor } from "~/helpers/localeNumberFormat";

export const COST_DECIMAL_PLACES = 4;

/** Decimal half-up rounding for locale-independent output (e.g. CSV columns), matching the server. */
export const roundMoneyHalfUp = (value: number, maxDecimals = 2): number =>
	Number(
		new Intl.NumberFormat("en-US", {
			maximumFractionDigits: maxDecimals,
			minimumFractionDigits: maxDecimals,
			useGrouping: false
		}).format(value)
	);

export const roundAwayNegativeZero = (value: number, decimals: number): number => {
	const rounded = Number(value.toFixed(decimals));
	return rounded === 0 ? 0 : rounded;
};

const moneyFormat = (value: number, currency: string, maxDecimals = 2) =>
	// Intl rounds the decimal this double round-trips to (2.405 -> $2.41); toFixed here only decides -0 vs 0.
	formatLocaleNumber(roundAwayNegativeZero(value, maxDecimals) === 0 ? 0 : value, {
		...narrowCurrencySymbolFor(currency),
		currency: currency,
		maximumFractionDigits: maxDecimals,
		minimumFractionDigits: 2,
		style: "currency"
	});

const MINIMUM_CURRENCY_FRACTION_DIGITS = 2;
const EXACT_AMOUNT_TEXT = /^(-?)(\d+)(?:\.(\d*))?$/;

const padExactAmountText = (amountText: string): string => {
	const match = EXACT_AMOUNT_TEXT.exec(amountText);
	if (!match) return amountText;
	const sign = match[1];
	const whole = match[2];
	const fraction = (match[3] ?? "").padEnd(MINIMUM_CURRENCY_FRACTION_DIGITS, "0");
	return `${sign}${whole}.${fraction}`;
};

export const formatExactMoneyText = (amountText: string, currency = "USD"): string =>
	formatLocaleCurrencyParts(currency)
		.filter((part) => part.type !== "decimal" && part.type !== "fraction")
		.map((part) => (part.type === "integer" ? padExactAmountText(amountText) : part.value))
		.join("");

export const formatMoneyOrBlank = (value: number | null | undefined): string =>
	value === null || value === undefined ? "" : moneyFormat(value, "USD");

export const PENDING_MONEY = "—";

export const formatMoneyOrPending = (value: number, hasServerTotals: boolean): string =>
	hasServerTotals ? moneyFormat(value, "USD") : PENDING_MONEY;

export const formatMoneyOrDash = (value: number | null | undefined, maxDecimals = 2): string =>
	value === null || value === undefined ? PENDING_MONEY : moneyFormat(value, "USD", maxDecimals);

/** Currency-formatted base value (no per-time suffix). */
export const formatOverheadRate = (n: number): string => `$${n.toFixed(COST_DECIMAL_PLACES)}`;

/** Side-panel display: `$x.xxxx/sqft/wk`. */
export const formatOverheadRatePerSqFtPerWeek = (n: number): string => `${formatOverheadRate(n)}/sqft/wk`;

/** Variance display: signed `+$1,234.00` / `-$1,234.00` / `$0.00`. */
export const formatSignedMoney = (value: number): string => {
	const normalized = roundAwayNegativeZero(value, 2);
	if (normalized === 0) return moneyFormat(0, "USD");
	const sign = normalized > 0 ? "+" : "-";
	return `${sign}${moneyFormat(Math.abs(value), "USD")}`;
};

export default moneyFormat;
